import { AUTH_TOKEN_KEY, AUTH_TOKEN_SESSION, generateAuthDataFromResponse, getInitialAuth } from 'contexts/AuthContext'
import RequestManager from './RequestManager'
import * as nativeApp from 'utils/nativeApp'
import { saveState } from 'utils/storage'
import { getUuid } from 'utils/uuid'
import * as urls from './urls'

const uuid = getUuid()

interface Options {
  body?: any
  form?: any
  signal?: AbortSignal
  credentials?: boolean
  withoutToken?: boolean
}

type Method = 'GET' | 'POST' | 'DELETE' | 'PATCH'

type RequestResponse<T> = Promise<{
  ok: boolean
  status?: number
  data: T
}>

type Request<T = any> = (resource: string, method: Method, options: Options) => RequestResponse<T>

const request: Request = async (
  resource = '',
  method = 'GET',
  { body = undefined, form = undefined, signal = undefined, credentials = false, withoutToken = false }
) => {
  const isBlocked = RequestManager.isBlocked.get()
  if (isBlocked) {
    new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (RequestManager.isBlocked.get() === false) {
          clearInterval(intervalId)
          resolve(undefined)
        }
      }, 1000)
    })
  }

  const token = RequestManager.jwt.get()
  const appName = nativeApp.getAppName()
  const headers = new Headers({
    'Accept': 'application/json',
    'X-Client-Hash': uuid,
    'X-App-Version': appName,
    //Ustawić kolejność en-us -> pl w zalenosci od wybranego jezyka!!
    'Accept-Language': 'en-us, pl;q=0.7'
  })

  if (!withoutToken && token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const init: RequestInit = {
    method,
    headers,
  }

  if (signal) init.signal = signal
  if (credentials) init.credentials = 'include'

  if (form) {
    init.body = form
  } else if (body) {
    headers.append('content-type', 'application/json')
    init.headers = headers
    init.body = JSON.stringify(body)
  }

  try {
    let response = await fetch(resource, init)
    const invalidToken = response.headers.get('x-token-invalid')

    if (invalidToken) {
      RequestManager.isBlocked.set(true)
      const refreshTokenResponse = await POST<auth.BasicLoginResponse>(urls.refresh, { credentials: true, withoutToken: true })
      if (refreshTokenResponse.ok) {
        const newToken = refreshTokenResponse.data.token.accessToken
        const data = generateAuthDataFromResponse(refreshTokenResponse.data)
        saveState(data, AUTH_TOKEN_SESSION, AUTH_TOKEN_KEY)
        RequestManager.jwt.set(newToken)
        headers.set('Authorization', `Bearer ${newToken}`)
        init.headers = headers
        response = await fetch(resource, init)

      } else {
        RequestManager.jwt.destroy()
        saveState(getInitialAuth(false), AUTH_TOKEN_SESSION, AUTH_TOKEN_KEY)
        window.location.reload()
      }
      RequestManager.isBlocked.set(false)
    }

    const type = response.headers.get('content-type')
    const data = (method === 'DELETE' && !response.bodyUsed)
      ? true
      : type?.includes('json')
        ? await response.json()
        : await response.text()
    return {
      ok: response.ok,
      status: response.status,
      data
    }

  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw new Error(`Request failed with message: ${message}`)
  }
}

export const GET = <T = any>(resource: string, options: Options = {}): RequestResponse<T> => request(resource, 'GET', options)
export const DELETE = <T = any>(resource: string): RequestResponse<T> => request(resource, 'DELETE', { credentials: true })
export const POST = <T = any>(resource = '', options: Options = {}): RequestResponse<T> => request(resource, 'POST', options)
export const PATCH = <T = any>(resource = '', options: Options = {}): RequestResponse<T> => request(resource, 'PATCH', options)
